import { useState } from 'react';
import { useUserInfo } from '@/hooks';
import { Buttons } from './components/Buttons';
import useDerivations from './hooks/useDerivations';
import { Derivation } from './interfaces/Derivation';
import useNewDerivations from './hooks/useNewDerivations';
import { DerivationStatus } from './enums/DerivationStatus';
import { DerivationList } from './components/DerivationList';
import { DerivationChat } from './components/DerivationChat';
import { DerivationFilter } from './components/DerivationFilter';
import { DerivationDetails } from './components/DerivationDetails';
import { useDerivationSorting } from './hooks/useDerivationSorting';
import { useDerivationActions } from './hooks/useDerivationActions';

const Sila = () => {
  const userInfo = useUserInfo();
  const currentUserId = userInfo?.id ? String(userInfo.id) : '';
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [assignedDerivations, setAssignedDerivations] = useState<Derivation[]>(
    []
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedDerivation, setSelectedDerivation] =
    useState<Derivation | null>(null);

  const [selectedView, setSelectedView] = useState<DerivationStatus>(
    DerivationStatus.PENDING
  );
  const {
    derivations,
    isLoading,
    totalItems,
    totalPages,
    currentPage: page,
    pageSize: currentPageSize,
  } = useDerivations({
    filters: {
      status: selectedView.toString(),
      user:
        selectedView === DerivationStatus.ASSIGNED ? currentUserId : undefined,
    },
    page: currentPage,
    pageSize: pageSize,
  });

  const { newDerivations, removeNewTag } = useNewDerivations(
    derivations,
    isLoading
  );
  const { takeDerivation } = useDerivationActions();

  const handleSelectDerivation = (derivation: Derivation) => {
    setSelectedDerivation(derivation);
    removeNewTag(derivation.id);
  };

  const handleTakeDerivation = async () => {
    if (selectedDerivation && selectedView === DerivationStatus.PENDING) {
      try {
        const updatedDerivation = await takeDerivation(
          selectedDerivation,
          currentUserId
        );
        setAssignedDerivations([...assignedDerivations, updatedDerivation]);
        setSelectedDerivation(updatedDerivation);
        setSelectedView(DerivationStatus.ASSIGNED);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleDeselect = () => {
    setSelectedDerivation(null);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setSelectedDerivation(null);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    setSelectedDerivation(null);
  };

  const handleViewChange = (newView: DerivationStatus) => {
    setSelectedView(newView);
    setCurrentPage(0);
    setSelectedDerivation(null);
  };

  return (
    <div className='flex items-start w-full h-screen justify-start overflow-hidden'>
      <div className='flex w-full h-full gap-6 p-6'>
        <div className='flex flex-col bg-gray-100 w-1/3 h-full rounded-xl overflow-hidden'>
          <DerivationFilter
            selectedView={selectedView}
            setSelectedView={handleViewChange}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />
          <DerivationList
            isLoading={isLoading}
            newDerivations={newDerivations}
            onSelect={handleSelectDerivation}
            selectedDerivation={selectedDerivation}
            derivations={useDerivationSorting(derivations, sortOrder)}
            totalItems={totalItems}
            totalPages={totalPages}
            currentPage={page}
            pageSize={currentPageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
        <div className='flex flex-col w-2/3 p-5 bg-white rounded-lg shadow-lg h-full overflow-hidden'>
          {selectedDerivation ? (
            <div className='flex flex-col h-full'>
              <div className='flex-shrink-0 mb-4'>
                <DerivationDetails
                  selectedDerivation={selectedDerivation}
                  onDeselect={handleDeselect}
                />
              </div>

              <div className='flex flex-col flex-grow min-h-0 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden'>
                <div className='flex-grow overflow-hidden'>
                  <DerivationChat
                    derivationId={selectedDerivation.id}
                    description={selectedDerivation.description}
                    sender={selectedDerivation.from}
                    contentHeight='100%'
                  />
                </div>

                <div className='border-t border-gray-200 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] p-3'>
                  <Buttons
                    selectedDerivation={selectedDerivation}
                    handleDeselect={handleDeselect}
                    onTakeDerivation={handleTakeDerivation}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className='flex flex-grow justify-center items-center'>
              <p className='text-gray-500 italic'>
                Seleccione una derivación primero.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sila;
